import { UAParser } from "ua-parser-js";

import type { ApplyClickResponseDto, JobItemDto } from "@artha/api-contracts";
import type { PublicJobAlertStorage } from "@artha/types";

import { isAcgLinkageExperimentEnabled } from "@/lib/analytics/config";
import { getPublicJobAlertDraftStorageKey } from "@/modules/job-alerts";

type ApplyClickPortalContext = {
  portalId?: string;
  domainName?: string;
  clientName?: string;
  userId?: string;
  userEmail?: string;
};

type _ApplyClickApiResponse = {
  success?: boolean;
  message?: string;
  data?: ApplyClickResponseDto;
};

const SESSION_STORAGE_KEY = "applyClickSessionId";
const CLICK_ATTEMPT_ID_PREFIX = "acgx_";

const createClickAttemptId = (): string => {
  const randomPart =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${CLICK_ATTEMPT_ID_PREFIX}${randomPart}`;
};

const normalizeDeviceType = (value?: string) => {
  if (value === "mobile" || value === "tablet") return value;
  return "desktop";
};

const parseUtmParams = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
  };
};

const isValidUrl = (value?: string): value is string => {
  if (!value) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return "unknown";
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const nextId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, nextId);
    return nextId;
  } catch {
    return "unknown";
  }
};

const isLinkedInInAppBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /LinkedInApp/i.test(navigator.userAgent);
};

export type ApplyRedirectOptions = {
  target?: "new-tab" | "same-tab";
};

const getLocalStorageUserId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const [usernameSegment] = window.location.pathname
      .split("/")
      .filter(Boolean);
    if (!usernameSegment) return undefined;

    const raw = window.localStorage.getItem(
      getPublicJobAlertDraftStorageKey(usernameSegment),
    );
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as PublicJobAlertStorage;
    return typeof parsed?.externalId === "string"
      ? parsed.externalId
      : undefined;
  } catch {
    return undefined;
  }
};

const buildTelemetry = (context?: ApplyClickPortalContext) => {
  if (typeof window === "undefined") return undefined;
  const parser = new UAParser();
  const result = parser.getResult();
  const osLabel = [result.os.name, result.os.version].filter(Boolean).join(" ");
  const user =
    context?.userId || context?.userEmail
      ? { id: context.userId, email: context.userEmail }
      : undefined;

  return {
    device: {
      type: normalizeDeviceType(result.device.type),
      vendor: result.device.vendor || undefined,
      model: result.device.model || undefined,
      os: osLabel || undefined,
      screenResolution: window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : "unknown",
    },
    browser: {
      name: result.browser.name || undefined,
      version: result.browser.version || undefined,
      engine: result.engine.name || undefined,
    },
    session: {
      id: getOrCreateSessionId(),
      referrer: document.referrer || "unknown",
      utmParams: parseUtmParams(window.location.search),
    },
    user,
    browserLanguage: navigator.language || undefined,
  };
};

export const getJobRedirectUrl = (
  baseUrl: string,
  params: {
    clientId?: string;
    clientName?: string;
    domainName?: string;
    source?: string;
    userId?: string | null;
    click_attempt_id?: string;
  },
) => {
  if (!baseUrl) return "";
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.append(key, value);
    }
  });
  return url.toString();
};
export const useApplyClick = (context?: ApplyClickPortalContext) => {
  const trackAndRedirect = async (
    job: JobItemDto,
    onPrepared?: (clickAttemptId?: string) => void,
    options: ApplyRedirectOptions = {},
  ) => {
    if (!job.id && !job.ref_id) {
      return;
    }

    const clickAttemptId = isAcgLinkageExperimentEnabled()
      ? createClickAttemptId()
      : undefined;
    const linkedInInAppBrowser = isLinkedInInAppBrowser();
    const useSameTab = options.target === "same-tab";

    // Pre-opening a blank tab breaks redirects in LinkedIn's in-app browser.
    const newTab =
      typeof window !== "undefined" && !linkedInInAppBrowser && !useSameTab
        ? window.open("", "_blank")
        : null;

    const domainName =
      context?.domainName ||
      (typeof window !== "undefined" ? window.location.hostname : undefined);

    const requestParams = {
      clientId: context?.portalId,
      clientName: context?.clientName,
      domainName: context?.domainName,
      source: "artha-link",
      userId: getLocalStorageUserId(),
      click_attempt_id: clickAttemptId,
    };
    const _payload = {
      job_id: job.id,
      ref_id: job.ref_id || undefined,
      redirect_url: isValidUrl(job.apply_url) ? job.apply_url : undefined,
      portal_id: context?.portalId,
      domain_name: domainName,
      client_name: context?.clientName,
      telemetry: buildTelemetry(context),
    };
    try {
      // const { ok, data } = await apiFetch<_ApplyClickApiResponse>(
      //   "/jobs/apply-click",
      //   {
      //     method: "POST",
      //     body: JSON.stringify(_payload),
      //   }
      // );
      // const redirectUrl = ok && data?.success ? data?.data?.redirect_url : undefined;
      const applicationUrl = isValidUrl(job.url)
        ? job.url
        : isValidUrl(job.apply_url)
          ? job.apply_url
          : "";
      const redirectUrl = getJobRedirectUrl(applicationUrl, requestParams);
      if (redirectUrl && isValidUrl(redirectUrl)) {
        try {
          onPrepared?.(clickAttemptId);
        } catch {
          // Observation must never block a revenue-producing redirect.
        }
        if (newTab) {
          newTab.location.href = redirectUrl;
        } else if (linkedInInAppBrowser || useSameTab) {
          window.location.assign(redirectUrl);
        } else {
          const popup = window.open(redirectUrl, "_blank");
          if (!popup) {
            window.location.assign(redirectUrl);
          }
        }
      } else {
        newTab?.close();
      }
    } catch {
      newTab?.close();
    }
  };

  return { trackAndRedirect };
};

export type { ApplyClickPortalContext };
